import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import styles from './AppLinkPicker.module.css'

const KINDS = [
    {
        key: 'story',
        label: 'Stories',
        singular: 'story',
        emptyIcon: 'mdi:book-open-blank-variant-outline',
    },
    {
        key: 'step',
        label: 'Steps',
        singular: 'step',
        emptyIcon: 'mdi:stairs',
    },
]

const AppLinkPicker = ({
    open = false,
    items = [],
    loading = false,
    onSelect,
    onClose,
}) => {
    const [kind, setKind] = useState('story')
    const [search, setSearch] = useState('')
    const [activeIndex, setActiveIndex] = useState(0)
    const searchRef = useRef(null)
    const listRef = useRef(null)
    const panelRef = useRef(null)

    const counts = useMemo(() => {
        const next = { story: 0, step: 0 }
        for (const item of items || []) {
            if (item?.kind === 'story') next.story += 1
            if (item?.kind === 'step') next.step += 1
        }
        return next
    }, [items])

    const activeKind = KINDS.find((tab) => tab.key === kind) || KINDS[0]

    useEffect(() => {
        if (!open) return
        setKind('story')
        setSearch('')
        setActiveIndex(0)
        const t = window.setTimeout(() => searchRef.current?.focus(), 30)
        return () => window.clearTimeout(t)
    }, [open])

    useEffect(() => {
        if (!open) return undefined
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = prev
        }
    }, [open])

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        return (items || []).filter((item) => {
            if (item.kind !== kind) return false
            if (!q) return true
            const crumbText = Array.isArray(item.crumbs)
                ? item.crumbs.join(' ')
                : String(item.secondary || '')
            return (
                item.title.toLowerCase().includes(q) ||
                crumbText.toLowerCase().includes(q)
            )
        })
    }, [items, kind, search])

    useEffect(() => {
        setActiveIndex(0)
    }, [kind, search])

    useEffect(() => {
        if (!open) return undefined
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                e.stopPropagation()
                onClose?.()
                return
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActiveIndex((i) =>
                    filtered.length ? Math.min(i + 1, filtered.length - 1) : 0
                )
                return
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActiveIndex((i) => Math.max(i - 1, 0))
                return
            }
            if (e.key === 'Enter') {
                const item = filtered[activeIndex]
                if (!item) return
                e.preventDefault()
                onSelect?.(item)
            }
        }
        window.addEventListener('keydown', onKey, true)
        return () => window.removeEventListener('keydown', onKey, true)
    }, [open, filtered, activeIndex, onClose, onSelect])

    useEffect(() => {
        const el = listRef.current?.querySelector(
            `[data-index="${activeIndex}"]`
        )
        el?.scrollIntoView?.({ block: 'nearest' })
    }, [activeIndex])

    if (!open) return null

    return createPortal(
        <div
            className={styles.overlay}
            data-app-link-picker-overlay
            onMouseDown={(e) => {
                e.preventDefault()
                if (e.target === e.currentTarget) onClose?.()
            }}
            role="presentation"
        >
            <div
                ref={panelRef}
                className={styles.panel}
                role="dialog"
                aria-modal="true"
                aria-label="Link story or step"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className={styles.header}>
                    <div className={styles.headerText}>
                        <div className={styles.title}>Link story or step</div>
                        <div className={styles.subtitle}>
                            Choose an item to insert into the description
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.closeBtn}
                        onClick={onClose}
                        title="Close"
                        aria-label="Close"
                    >
                        <Icon icon="mdi:close" width={18} height={18} />
                    </button>
                </div>

                <div className={styles.segment} role="tablist">
                    {KINDS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={kind === tab.key}
                            className={`${styles.segmentBtn} ${
                                kind === tab.key ? styles.segmentBtnActive : ''
                            }`}
                            onClick={() => setKind(tab.key)}
                        >
                            <span>{tab.label}</span>
                            <span className={styles.count}>
                                {counts[tab.key]}
                            </span>
                        </button>
                    ))}
                </div>

                <div className={styles.searchWrap}>
                    <label className={styles.searchField}>
                        <Icon
                            icon="mdi:magnify"
                            width={18}
                            height={18}
                            className={styles.searchIcon}
                        />
                        <input
                            ref={searchRef}
                            className={styles.searchInput}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={`Search ${activeKind.label.toLowerCase()}...`}
                            type="text"
                            autoComplete="off"
                        />
                        {search ? (
                            <button
                                type="button"
                                className={styles.clearBtn}
                                onClick={() => {
                                    setSearch('')
                                    searchRef.current?.focus()
                                }}
                                aria-label="Clear search"
                            >
                                <Icon
                                    icon="mdi:close-circle"
                                    width={16}
                                    height={16}
                                />
                            </button>
                        ) : null}
                    </label>
                </div>

                <div ref={listRef} className={styles.list}>
                    {loading ? (
                        <div className={styles.empty}>
                            <Icon
                                icon="line-md:loading-loop"
                                width={26}
                                height={26}
                                className={styles.emptyIcon}
                            />
                            <div className={styles.emptyTitle}>Loading...</div>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className={styles.empty}>
                            <div className={styles.emptyTitle}>
                                No {activeKind.label.toLowerCase()} found
                            </div>
                            <div className={styles.emptyHint}>
                                {search.trim()
                                    ? `Nothing matches “${search.trim()}”`
                                    : `No ${activeKind.singular} items available`}
                            </div>
                        </div>
                    ) : (
                        filtered.map((item, index) => {
                            const crumbs = Array.isArray(item.crumbs)
                                ? item.crumbs.filter(Boolean)
                                : String(item.secondary || '')
                                      .split(/\s*›\s*/)
                                      .filter(Boolean)
                            const lastCrumbIdx = crumbs.length - 1

                            return (
                                <button
                                    key={`${item.kind}-${item.id}`}
                                    type="button"
                                    data-index={index}
                                    className={`${styles.item} ${
                                        index === activeIndex
                                            ? styles.itemActive
                                            : ''
                                    }`}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onClick={() => onSelect?.(item)}
                                >
                                    <span className={styles.itemBody}>
                                        <span className={styles.itemTop}>
                                            <span className={styles.itemTitle}>
                                                {item.title}
                                            </span>
                                            <span className={styles.kindTag}>
                                                {item.kind === 'step'
                                                    ? 'Step'
                                                    : 'Story'}
                                            </span>
                                        </span>
                                        {crumbs.length > 0 ? (
                                            <span
                                                className={styles.breadcrumb}
                                                aria-label={crumbs.join(' › ')}
                                            >
                                                {crumbs.map(
                                                    (crumb, crumbIdx) => (
                                                        <span
                                                            key={`${item.id}-crumb-${crumbIdx}`}
                                                            className={
                                                                styles.crumbPart
                                                            }
                                                        >
                                                            {crumbIdx > 0 ? (
                                                                <span
                                                                    className={
                                                                        styles.crumbSep
                                                                    }
                                                                    aria-hidden="true"
                                                                >
                                                                    /
                                                                </span>
                                                            ) : null}
                                                            <span
                                                                className={`${styles.crumbLabel} ${
                                                                    crumbIdx ===
                                                                    lastCrumbIdx
                                                                        ? styles.crumbCurrent
                                                                        : ''
                                                                }`}
                                                            >
                                                                {crumb}
                                                            </span>
                                                        </span>
                                                    )
                                                )}
                                            </span>
                                        ) : null}
                                    </span>
                                </button>
                            )
                        })
                    )}
                </div>

                <div className={styles.footer}>
                    <span className={styles.kbdHint}>
                        <kbd>↑</kbd>
                        <kbd>↓</kbd>
                        navigate
                    </span>
                    <span className={styles.kbdHint}>
                        <kbd>Enter</kbd>
                        select
                    </span>
                    <span className={styles.kbdHint}>
                        <kbd>Esc</kbd>
                        close
                    </span>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default AppLinkPicker
